import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { matchesQuery } from '@/utils/listingPicker';
import { ccySym, frDate } from '@/utils/currencyUtils';
import { tripPriceDisplay } from '@/utils/tripPriceUtils';
import '@/styles/listing-chooser.css';

/*
 * Choix de « mon annonce » face à l'annonce visée : page d'une annonce (carte d'action) et fenêtre « Faire une
 * proposition ». Un seul affichage pour les deux ; règles dans utils/listingPicker.js, état dans
 * hooks/useListingChoice.js. Bidirectionnel : mes colis (meSide 'sender') ou mes trajets (meSide 'carrier').
 */

const kg = (n) => `${Number(n).toLocaleString('fr-FR')} kg`;
const fmtAmt = (n) => Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const LIMIT = 5;

/** Une de mes annonces : route, poids, prix ; « Déjà proposé » ; « Trop lourd » grisée par la couleur et non choisissable. */
function ListingRow({ entry, selected, onSelect, meSide }) {
  const { listing: l, fit, proposed, selectable } = entry;
  const parts = [];
  if (!selectable) {
    parts.push(meSide === 'sender'
      ? `${kg(fit.weight)} · il reste ${kg(fit.room)} sur ce trajet`
      : `${kg(fit.room)} de libre · colis de ${kg(fit.weight)}`);
  } else if (meSide === 'sender') {
    if (l.weight_kg) parts.push(kg(l.weight_kg));
    if (Array.isArray(l.constraints) && l.constraints.includes('fragile')) parts.push('Fragile');
    else if (Array.isArray(l.categories) && l.categories.length > 0) parts.push(l.categories[0]);
  } else {
    const poids = l.available_weight_kg || l.weight_kg;
    if (poids) parts.push(kg(poids));
    if (l.departure_date) parts.push(frDate(l.departure_date));
  }
  const price = meSide === 'sender'
    ? (l.price_offered ? `${fmtAmt(Number(l.price_offered))} ${ccySym(l.primary_currency || 'EUR')}` : null)
    : tripPriceDisplay(l).display;
  const choose = () => { if (selectable) onSelect(l.id); };
  return (
    <div
      data-testid={`dp-listing-card-${l.id}`}
      className={`dp-listing-card${selected ? ' active' : ''}${selectable ? '' : ' dp-listing-card--off'}`}
      onClick={choose}
      role="radio"
      aria-checked={selected}
      aria-disabled={!selectable}
      tabIndex={selectable ? 0 : -1}
      onKeyDown={e => e.key === 'Enter' && choose()}
    >
      <span className="dp-listing-card-dot" aria-hidden="true" />
      <div className="dp-listing-card-body">
        <div className="dp-listing-card-title">
          {l.departure_city} → {l.arrival_city}
          {proposed && selectable && <span className="dp-listing-badge dp-listing-badge--done">Déjà proposé</span>}
        </div>
        {parts.length > 0 && <div className="dp-listing-card-sub">{parts.join(' · ')}</div>}
      </div>
      {selectable
        ? price && <span className="dp-listing-card-price">{price}</span>
        : <span className="dp-listing-badge dp-listing-badge--off">Trop lourd</span>}
    </div>
  );
}

/**
 * Liste ouverte : recherche au-delà de 5 annonces, « Compatibles » (même trajet, poids qui passe) ou « Tous »,
 * groupes « Même trajet » puis « Autres », cinq lignes puis « Voir les N autres ».
 */
function ListingList({ entries, selectedId, onSelect, meSide }) {
  const [query, setQuery] = useState('');
  const [onlyCompatible, setOnlyCompatible] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const noun = meSide === 'sender' ? 'colis' : 'trajets';
  const compatible = e => e.fit.sameRoute && e.selectable;
  const nCompatible = entries.filter(compatible).length;
  const showFilter = nCompatible > 0 && nCompatible < entries.length;
  const searching = query.trim() !== '';
  const shown = entries.filter(e => matchesQuery(e.listing, query)
    && (searching || !showFilter || !onlyCompatible || compatible(e) || e.listing.id === selectedId));
  const visible = shown.filter((e, i) => expanded || searching || i < LIMIT || e.listing.id === selectedId);
  const hidden = shown.length - visible.length;
  const count = same => shown.filter(e => e.fit.sameRoute === same).length;
  const grouped = count(true) > 0 && count(false) > 0;
  let group = null;

  return (
    <div className="dp-listing-cards">
      {entries.length > LIMIT && (
        <input
          type="search"
          className="dp-listing-search"
          data-testid="dp-listing-search"
          placeholder={meSide === 'sender' ? 'Ville, contenu… (Dakar, fragile)' : 'Ville… (Dakar)'}
          aria-label={`Rechercher parmi vos ${noun}`}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      )}
      {showFilter && !searching && (
        <div className="dp-listing-filters" role="group" aria-label={`Filtrer vos ${noun}`}>
          <button type="button" data-testid="dp-listing-filter-compatible" aria-pressed={onlyCompatible}
            className={`dp-listing-filter${onlyCompatible ? ' active' : ''}`} onClick={() => setOnlyCompatible(true)}>
            Compatibles · {nCompatible}
          </button>
          <button type="button" data-testid="dp-listing-filter-all" aria-pressed={!onlyCompatible}
            className={`dp-listing-filter${onlyCompatible ? '' : ' active'}`} onClick={() => setOnlyCompatible(false)}>
            Tous · {entries.length}
          </button>
        </div>
      )}
      {visible.map(e => {
        const g = e.fit.sameRoute;
        const header = grouped && g !== group;
        group = g;
        return (
          <React.Fragment key={e.listing.id}>
            {header && (
              <div className="dp-listing-group" data-testid="dp-listing-group">
                {g ? `Même trajet · ${count(true)}` : `Autres ${noun} · ${count(false)}`}
              </div>
            )}
            <ListingRow entry={e} selected={e.listing.id === selectedId} onSelect={onSelect} meSide={meSide} />
          </React.Fragment>
        );
      })}
      {searching && shown.length === 0 && (
        <div className="dp-hint">Aucun de vos {noun} ne correspond à « {query.trim()} ».</div>
      )}
      {hidden > 0 && (
        <button type="button" className="dp-listing-more" data-testid="dp-listing-more" onClick={() => setExpanded(true)}>
          Voir {hidden === 1 ? `l'autre` : `les ${hidden} autres`}
        </button>
      )}
      {expanded && !searching && shown.length > LIMIT && (
        <button type="button" className="dp-listing-more" onClick={() => setExpanded(false)}>
          Réduire
        </button>
      )}
    </div>
  );
}

/**
 * Choix de mon annonce : replié sur l'annonce choisie (« Votre colis », « Changer »), ouvert sinon.
 * `eta` : « J+N » du trajet visé (côté expéditeur) ; `createHref` : publier une nouvelle annonce, lien toujours
 * présent dans la liste ouverte (`createInNewTab` : dans un nouvel onglet, pour ne pas perdre une fenêtre en cours).
 */
export default function ListingChooser({
  entries, selected, selectedId, onSelect, meSide, loading, eta, createHref, createInNewTab = false,
}) {
  const [open, setOpen] = useState(!selectedId);
  useEffect(() => { setOpen(!selectedId); }, [selectedId]);

  if (loading) return <div className="listing-chooser"><div className="dp-hint">Chargement de vos annonces…</div></div>;

  const l = selected?.listing;
  return (
    <div className="listing-chooser">
      {selected && !open ? (
        <div className="dp-escrow-colis-summary">
          <span className="dp-sidebar-colis-label">{meSide === 'sender' ? 'Votre colis' : 'Votre trajet'}</span>
          <div className="dp-selected-summary" data-testid="dp-selected-summary">
            <div className="dp-selected-summary-info">
              <div className="dp-selected-summary-route">{l.departure_city} → {l.arrival_city}</div>
              <div className="dp-selected-summary-sub">
                {meSide === 'sender'
                  ? `${kg(l.weight_kg)}${eta ? ` · estimation ${eta}` : ''}`
                  : `${kg(l.available_weight_kg)} · ${tripPriceDisplay(l).display}`}
                {selected.fit.sameRoute && <span className="dp-listing-badge dp-listing-badge--match">même trajet</span>}
              </div>
            </div>
            {entries.length > 1 && (
              <button type="button" className="dp-change-btn" data-testid="dp-change-listing" onClick={() => setOpen(true)}>
                Changer
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="dp-escrow-colis-list">
          <div className="dp-escrow-list-header">
            <span className="dp-label">{meSide === 'sender' ? 'Choisissez votre colis' : 'Choisissez votre trajet'}</span>
            {eta && <span className="dp-escrow-list-eta">départ {eta}</span>}
            {selected && (
              <button type="button" className="dp-escrow-list-close" onClick={() => setOpen(false)} aria-label="Fermer la liste">
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </div>
          {entries.length > 0 && (
            <ListingList entries={entries} selectedId={selectedId} meSide={meSide}
              onSelect={id => { onSelect(id); setOpen(false); }} />
          )}
          {entries.length === 0 && <div className="dp-hint">Aucune annonce publiée pour l'instant.</div>}
        </div>
      )}
      {createHref && (
        <a
          className="dp-create-listing"
          data-testid="dp-create-listing"
          href={createHref}
          {...(createInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          <Plus size={14} aria-hidden="true" />
          {meSide === 'sender' ? 'Publier un nouveau colis' : 'Publier un nouveau trajet'}
          {createInNewTab && <span className="dp-create-listing-note">(nouvel onglet ; rouvrez ensuite cette fenêtre)</span>}
        </a>
      )}
    </div>
  );
}
